import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { acquireAccessToken } from './msalConfig';
import { queryClient } from './queryClient';
import type { ApiError } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Keep in sync with `usersKeys.me()` in `lib/api/admin.ts`. Inlined here so
// apiClient doesn't import from api/admin (which itself imports apiClient).
const USERS_ME_QUERY_KEY = ['users', 'me'] as const;

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      // NOTE: this default applies to FormData bodies too, and axios reacts by
      // serializing them to JSON — the request arrives with no file and the endpoint
      // rejects it as missing. Any multipart upload must override this per call with
      // 'Content-Type': 'multipart/form-data' so the browser sets the boundary.
      // See massMigrationApi.validateCsv.
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    async (config) => {
      const token = await acquireAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<ApiError>) => {
      if (error.response) {
        const apiError: ApiError = {
          type: error.response.data?.type ?? 'https://tools.ietf.org/html/rfc7231#section-6.5.1',
          title: error.response.data?.title ?? 'An error occurred',
          status: error.response.status,
          detail: error.response.data?.detail,
          errors: error.response.data?.errors,
        };

        // Handle 401 Unauthorized — coerce AuthGuard to take over promptly.
        //
        // Any endpoint returning 401 means the access token is dead. Force a
        // refetch of /users/me so meQuery transitions into error state on its
        // next render; AuthGuard then swaps to the verification-failed page,
        // which auto-attempts silent forceRefresh and falls back to interactive
        // redirect (see NotAuthorizedPage + msalConfig.tryInteractiveTokenRecovery).
        //
        // Without this, a non-/users/me endpoint hitting 401 leaves AuthGuard
        // happy with its stale meQuery cache and the user is stuck on the
        // current route with a "Session Expired" error message until they
        // navigate elsewhere.
        if (error.response.status === 401) {
          console.warn('Unauthorized request — invalidating /users/me to trigger AuthGuard');
          void queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY });
        }

        return Promise.reject(apiError);
      }

      // Network error or timeout
      const networkError: ApiError = {
        type: 'https://tools.ietf.org/html/rfc7231#section-6.5.1',
        title: 'Network Error',
        status: 0,
        detail: error.message,
      };

      return Promise.reject(networkError);
    }
  );

  return client;
}

export const apiClient = createApiClient();

// Helper functions for common operations
export async function get<T>(url: string, params?: object): Promise<T> {
  const response = await apiClient.get<T>(url, { params });
  return response.data;
}

export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data);
  return response.data;
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
}

export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url);
  return response.data;
}
